/**
 * WhiteboardToolbar - Toolbar for Agora Interactive Whiteboard
 *
 * Provides drawing tools, shapes, colors, and other controls
 * for the white-web-sdk whiteboard.
 */

import React, { useState, useCallback } from 'react';
import {
  Pencil,
  Eraser,
  Scissors,
  MousePointer2,
  Type,
  Square,
  Circle,
  Minus,
  ArrowRight,
  Trash2,
  Undo2,
  Redo2,
  Hand,
} from 'lucide-react';

// Tool types supported by white-web-sdk
const TOOLS = {
  selector: 'selector',
  pencil: 'pencil',
  eraser: 'eraser',
  text: 'text',
  rectangle: 'rectangle',
  ellipse: 'ellipse',
  straight: 'straight',
  arrow: 'arrow',
  hand: 'hand',
};

// Preset colors
const COLORS = [
  '#000000', // Black
  '#FF0000', // Red
  '#FF6B00', // Orange
  '#FFD700', // Yellow
  '#00C853', // Green
  '#2196F3', // Blue
  '#9C27B0', // Purple
  '#FFFFFF', // White
];

// Stroke widths
const STROKE_WIDTHS = [2, 4, 6, 8, 12];

// Radius used by the partial-eraser (pencilEraser). Its hit area scales with
// strokeWidth; drawing widths (2–12) are too small to feel responsive, so we
// push a larger width while the eraser is selected and restore the user's
// drawing width when they pick another tool.
const ERASER_STROKE_WIDTH = 24;

// white-web-sdk appliance name for each internal tool key. Plain 'eraser'
// deletes whole objects (shapes, text, arrows, pencil strokes) on click.
// 'pencilEraser' partially erases (splits) pencil strokes only.
//
// The Line tool ('straight') is intentionally routed to the pencil appliance
// instead of the native 'straight' primitive so that the partial eraser can
// split user-drawn lines into pieces. Trade-off: the line is freehand, not a
// perfectly-snapped vector segment.
function applianceFor(toolName) {
  if (toolName === 'eraser') return 'eraser';
  if (toolName === 'partialEraser') return 'pencilEraser';
  if (toolName === 'straight') return 'pencil';
  return toolName;
}

function isEraserTool(toolName) {
  return toolName === 'eraser' || toolName === 'partialEraser';
}

// Persist last-used tool/color/stroke across whiteboard close/reopen cycles.
// Module-level so it survives component unmount but resets on page reload.
let _lastTool = 'pencil';
let _lastColor = '#FF0000';
let _lastStrokeWidth = 4;

export default function WhiteboardToolbar({ room, isHost = true }) {
  const [activeTool, setActiveTool] = useState(_lastTool);
  const [activeColor, setActiveColor] = useState(_lastColor);
  const [strokeWidth, setStrokeWidth] = useState(_lastStrokeWidth);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showStrokePicker, setShowStrokePicker] = useState(false);

  // On mount / room change, unconditionally force the persisted tool state
  // into the Netless SDK. The room's memberState can be out of sync with what
  // the UI displays — e.g. rejoining the same room UUID, Netless remembers the
  // previous appliance, but our module-level _lastTool may differ. Always
  // overwrite with the UI intent so the two match.
  //
  // Guarded on room.isWritable so the set doesn't silently fail right after
  // joinRoom (isWritable can be undefined briefly). Retries via the
  // onRoomStateChanged callback below once writable flips true.
  React.useEffect(() => {
    if (!room || !isHost) return;

    const applyPersistedState = () => {
      try {
        const applianceName = applianceFor(_lastTool);
        const appliedWidth = isEraserTool(_lastTool) ? ERASER_STROKE_WIDTH : _lastStrokeWidth;
        const r = parseInt(_lastColor.slice(1, 3), 16);
        const g = parseInt(_lastColor.slice(3, 5), 16);
        const b = parseInt(_lastColor.slice(5, 7), 16);
        room.setMemberState({
          currentApplianceName: applianceName,
          strokeColor: [r, g, b],
          strokeWidth: appliedWidth,
        });
        if (room.setViewMode) {
          room.setViewMode(_lastTool === 'hand' ? 'broadcaster' : 'follower');
        }
        return true;
      } catch (_) {
        return false;
      }
    };

    // Immediate attempt
    const appliedImmediately = room.isWritable === true && applyPersistedState();

    // Fallback: retry whenever writable state changes (joinRoom → isWritable
    // transitions from undefined → true asynchronously).
    if (!appliedImmediately && room.callbacks?.on) {
      let done = false;
      const onStateChanged = () => {
        if (done) return;
        if (room.isWritable === true && applyPersistedState()) {
          done = true;
          room.callbacks.off?.('onRoomStateChanged', onStateChanged);
        }
      };
      room.callbacks.on('onRoomStateChanged', onStateChanged);
      return () => {
        room.callbacks.off?.('onRoomStateChanged', onStateChanged);
      };
    }
  }, [room, isHost]);

  // Set the active tool on the whiteboard room
  const selectTool = useCallback((toolName) => {
    if (!room || !isHost) return;

    setActiveTool(toolName);
    _lastTool = toolName;
    setShowColorPicker(false);
    setShowStrokePicker(false);

    try {
      // Selector moves/resizes/duplicates are serialized commands. white-web-sdk
      // 2.16 sometimes has disableSerialization=true at this point (reset by an
      // earlier setWritable), which silently drops those commands so remote
      // users never see the transform. Re-assert it here.
      if (toolName === 'selector' && room.disableSerialization) {
        room.disableSerialization = false;
      }

      // Dynamic viewport broadcaster: whoever is actively using the Hand tool
      // broadcasts their pan/zoom to everyone else. Switching to any other
      // tool drops back to follower so the NEXT panner (host or presenter)
      // can take over and be seen by everyone — including the previous panner.
      if (room.setViewMode) {
        room.setViewMode(toolName === 'hand' ? 'broadcaster' : 'follower');
      }

      if (isEraserTool(toolName)) {
        // 'eraser'       → pencilEraser (partial erase of pencil strokes).
        // 'shapeEraser'  → plain eraser (deletes whole shapes / text / strokes on click).
        // Either eraser's hit area benefits from a larger strokeWidth, which we
        // restore to _lastStrokeWidth on the next non-eraser tool.
        room.setMemberState({
          currentApplianceName: applianceFor(toolName),
          strokeWidth: ERASER_STROKE_WIDTH,
        });
      } else {
        // Any drawing tool: restore the user's drawing strokeWidth in case the
        // previous tool was an eraser (which temporarily bumped it).
        room.setMemberState({
          currentApplianceName: toolName,
          strokeWidth: _lastStrokeWidth,
        });
      }
    } catch (err) {
      console.error('[WhiteboardToolbar] Error setting tool:', err);
    }
  }, [room, isHost]);

  // Set stroke color
  const selectColor = useCallback((color) => {
    if (!room || !isHost) return;

    setActiveColor(color);
    _lastColor = color;
    setShowColorPicker(false);

    try {
      // Convert hex to RGB array
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      room.setMemberState({ strokeColor: [r, g, b] });
    } catch (err) {
      console.error('[WhiteboardToolbar] Error setting color:', err);
    }
  }, [room, isHost]);

  // Set stroke width
  const selectStrokeWidth = useCallback((width) => {
    if (!room || !isHost) return;

    setStrokeWidth(width);
    _lastStrokeWidth = width;
    setShowStrokePicker(false);

    try {
      room.setMemberState({ strokeWidth: width });
    } catch (err) {
      console.error('[WhiteboardToolbar] Error setting stroke width:', err);
    }
  }, [room, isHost]);

  // Clear the whiteboard
  const clearBoard = useCallback(() => {
    if (!room || !isHost) return;

    try {
      room.cleanCurrentScene();
    } catch (err) {
      console.error('[WhiteboardToolbar] Error clearing board:', err);
    }
  }, [room, isHost]);

  // Undo last action. If canUndoSteps is 0, the serialization flag was true
  // when the action was recorded — WhiteboardContainer re-asserts it on every
  // room state change to keep the undo stack populated.
  const undo = useCallback(() => {
    if (!room) return;
    try {
      if (room.disableSerialization) room.disableSerialization = false;
      const steps = room.canUndoSteps ?? 0;
      if (steps === 0) {
        console.warn('[WhiteboardToolbar] Undo: no steps in stack (canUndoSteps=0)');
      }
      room.undo();
    } catch (err) {
      console.error('[WhiteboardToolbar] Error undoing:', err);
    }
  }, [room]);

  // Redo last undone action
  const redo = useCallback(() => {
    if (!room) return;
    try {
      if (room.disableSerialization) room.disableSerialization = false;
      const steps = room.canRedoSteps ?? 0;
      if (steps === 0) {
        console.warn('[WhiteboardToolbar] Redo: no steps in stack (canRedoSteps=0)');
      }
      room.redo();
    } catch (err) {
      console.error('[WhiteboardToolbar] Error redoing:', err);
    }
  }, [room]);

  if (!isHost) {
    // Read-only mode - no toolbar
    return null;
  }

  const ToolButton = ({ tool, icon: Icon, title }) => (
    <button
      onClick={() => selectTool(tool)}
      className={`p-2 rounded-lg transition-all ${
        activeTool === tool
          ? 'bg-[#6a1fbf] text-white shadow-lg'
          : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
      }`}
      title={title}
    >
      <Icon className="w-5 h-5" />
    </button>
  );

  return (
    <div className="absolute left-3 top-3 bottom-3 flex flex-col gap-0.5 p-1.5 bg-[#1a1f35]/95 backdrop-blur-sm rounded-xl border border-white/10 shadow-xl z-10 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20">
      {/* Selection & Pan Tools */}
      <ToolButton tool="selector" icon={MousePointer2} title="Select" />
      <ToolButton tool="hand" icon={Hand} title="Pan" />

      <div className="w-full h-px bg-white/10 my-1" />

      {/* Drawing Tools */}
      <ToolButton tool="pencil" icon={Pencil} title="Pencil" />
      <ToolButton tool="eraser" icon={Eraser} title="Eraser — click to delete any shape, line, text, or stroke" />
      <ToolButton tool="partialEraser" icon={Scissors} title="Partial pencil eraser — drag to split pencil strokes" />
      <ToolButton tool="text" icon={Type} title="Text" />

      <div className="w-full h-px bg-white/10 my-1" />

      {/* Shape Tools */}
      <ToolButton tool="straight" icon={Minus} title="Line" />
      <ToolButton tool="arrow" icon={ArrowRight} title="Arrow" />
      <ToolButton tool="rectangle" icon={Square} title="Rectangle" />
      <ToolButton tool="ellipse" icon={Circle} title="Ellipse" />

      <div className="w-full h-px bg-white/10 my-1" />

      {/* Color Picker */}
      <div className="relative">
        <button
          onClick={() => {
            setShowColorPicker(!showColorPicker);
            setShowStrokePicker(false);
          }}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all"
          title="Color"
        >
          <div
            className="w-5 h-5 rounded-full border-2 border-white/30"
            style={{ backgroundColor: activeColor }}
          />
        </button>

        {showColorPicker && (
          <div className="absolute left-full ml-2 top-0 p-3 bg-[#1a1f35] rounded-lg border border-white/10 shadow-xl grid grid-cols-4 gap-2 min-w-[120px]">
            {COLORS.map((color) => (
              <button
                key={color}
                onClick={() => selectColor(color)}
                className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                  activeColor === color ? 'border-[#6a1fbf] scale-110 ring-2 ring-[#6a1fbf]/50' : 'border-white/30'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        )}
      </div>

      {/* Stroke Width */}
      <div className="relative">
        <button
          onClick={() => {
            setShowStrokePicker(!showStrokePicker);
            setShowColorPicker(false);
          }}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all flex items-center justify-center"
          title="Stroke Width"
        >
          <div
            className="rounded-full bg-white"
            style={{ width: strokeWidth + 4, height: strokeWidth + 4 }}
          />
        </button>

        {showStrokePicker && (
          <div className="absolute left-full ml-2 top-0 p-2 bg-[#1a1f35] rounded-lg border border-white/10 shadow-xl flex flex-col gap-1">
            {STROKE_WIDTHS.map((width) => (
              <button
                key={width}
                onClick={() => selectStrokeWidth(width)}
                className={`px-3 py-1 rounded flex items-center gap-2 transition-colors ${
                  strokeWidth === width ? 'bg-[#6a1fbf]' : 'hover:bg-white/10'
                }`}
              >
                <div
                  className="rounded-full bg-white"
                  style={{ width: width + 2, height: width + 2 }}
                />
                <span className="text-xs text-white">{width}px</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-full h-px bg-white/10 my-1" />

      {/* Undo/Redo */}
      <button
        onClick={undo}
        className="p-2 rounded-lg bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white transition-all"
        title="Undo"
      >
        <Undo2 className="w-5 h-5" />
      </button>
      <button
        onClick={redo}
        className="p-2 rounded-lg bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white transition-all"
        title="Redo"
      >
        <Redo2 className="w-5 h-5" />
      </button>

      <div className="w-full h-px bg-white/10 my-1" />

      {/* Clear */}
      <button
        onClick={clearBoard}
        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
        title="Clear Board"
      >
        <Trash2 className="w-5 h-5" />
      </button>
    </div>
  );
}
